import React, { useState, useEffect } from "react"
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Tooltip,
  styled
} from "@mui/material"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import { useDispatch, useSelector } from "react-redux"
import Card from "components/Card/Card"
import AnalyticsCharts from "./components/AnalyticsCharts"
import AppPagination from "components/StyledComponents/AppPagination"
import { getAnalyticsSummaryAction, exportAnalyticsAction } from "redux/superAdmin/actions"
import { analyticsDashboardColumns } from "helpers/constant"
import { superAdminAction } from "redux/superAdmin/slice/user"
import { appActions } from "redux/app/slice/app"

const CustomTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  borderCollapse: "collapse !important",
  "& .MuiTableCell-root": {
    padding: "16px 24px 16px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: "none",
    boxSizing: "border-box",
    "&:last-child": {
      paddingRight: 0
    }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid #272a2c`,
    "&:last-child": {
      borderBottom: "none"
    }
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    position: "sticky",
    borderBottom: `2px solid #272a2c`,
    "&:last-child": {
      paddingRight: 0
    }
  }
}))

const StatCard = ({ label, value, loading = false, info = false }) => {
  return (
    <Card style={{ margin: 0, marginBottom: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
        <Typography variant="body2Regular" color="textSecondary">
          {label}
        </Typography>
        {info && (
          <Tooltip
            title="Utilization percentage is calculated based on the total number of cards and the total number of redemptions (Total Redemption Limit / Total Redeemed * 100)"
            arrow
            placement="top">
            <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary", cursor: "pointer" }} />
          </Tooltip>
        )}
      </Box>
      {loading ? (
        <CircularProgress size={24} sx={{ color: "secondary.main" }} />
      ) : (
        <Typography variant="h4" sx={{ fontWeight: 600, color: "secondary.main", mt: 1 }}>
          {value}
        </Typography>
      )}
    </Card>
  )
}

const AnalyticsDashboard = ({ exportTrigger = 0 }) => {
  const { userRestaurant } = useSelector((state) => state.user)
  const restaurantId = userRestaurant?.id
  const { startDate, endDate } = useSelector((store) => store.app.filters || {})
  const { analytics } = useSelector((state) => state.superAdmin)
  const dispatch = useDispatch()
  const [currentPage, setCurrentPage] = useState(1)
  const [isInitialized, setIsInitialized] = useState(false)
  const rowsPerPage = 10

  useEffect(() => {
    document.title = "Analytics Dashboard"
    dispatch(
      superAdminAction.setAnalytics({
        total_cards: 0,
        total_redemption_limit: 0,
        total_redeemed: 0,
        overall_utilization_percentage: 0,
        cards_breakdown: []
      })
    )
    dispatch(appActions.initFilters())
    setIsInitialized(true)
  }, [])

  useEffect(() => {
    if (isInitialized && startDate && endDate) {
      dispatch(getAnalyticsSummaryAction({ restaurantId, startDate, endDate }))
    }
  }, [isInitialized, startDate, endDate])

  useEffect(() => {
    if (exportTrigger > 0) {
      handleExport()
    }
  }, [exportTrigger])

  const handleExport = () => {
    if (!restaurantId || !startDate || !endDate) return
    dispatch(exportAnalyticsAction({ restaurantId, startDate, endDate }))
  }

  const paginatedRedemptions = (analytics?.cards_breakdown || []).slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  )

  return (
    <Box>
      {analytics?.spree && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.25,
            borderRadius: "12px",
            border: "1px solid",
            borderColor: analytics.spree.is_active
              ? "rgba(170, 142, 112, 0.5)"
              : "rgba(255,255,255,0.12)",
            backgroundColor: "rgba(255, 255, 255, 0.04)"
          }}>
          <Typography variant="body2Regular" color="text.secondary">
            {analytics.spree.is_active
              ? "Showing the active spree"
              : "Showing the last completed spree"}
            {analytics.spree.spree_start_date && analytics.spree.spree_end_date
              ? ` · ${analytics.spree.spree_start_date} → ${analytics.spree.spree_end_date}`
              : ""}
          </Typography>
        </Box>
      )}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {[
          { label: "Total Cards", value: analytics?.total_cards || 0, info: false },
          {
            label: "Total Redemption Limit",
            value: analytics?.total_redemption_limit || 0,
            info: false
          },
          { label: "Total Redeemed", value: analytics?.total_redeemed || 0, info: false },
          {
            label: "Utilization (%)",
            value: `${analytics?.overall_utilization_percentage || 0}%`,
            info: true
          }
        ].map((stat) => (
          <Grid key={stat.label} item xs={12} sm={6} md={3}>
            <StatCard label={stat.label} value={stat.value} info={stat.info} />
          </Grid>
        ))}
      </Grid>

      <AnalyticsCharts analyticsData={analytics} />

      <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
        Redemptions by Reward
      </Typography>

      {(analytics?.cards_breakdown || []).length === 0 ? (
        <Card>
          <Typography variant="body2Regular" color="textSecondary" sx={{ textAlign: "center" }}>
            No redemption data available for the selected date range.
          </Typography>
        </Card>
      ) : (
        <>
          <Paper>
            <TableContainer sx={{ maxHeight: 700 }}>
              <CustomTable stickyHeader>
                <TableHead>
                  <TableRow>
                    {analyticsDashboardColumns?.map((column) => (
                      <TableCell key={column.id} align={column.align}>
                        <Typography variant="body2Regular" style={{ opacity: 0.6 }}>
                          {column.label}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRedemptions?.map((item, index) => (
                    <TableRow key={index} sx={{ "&:hover": { backgroundColor: "action.hover" } }}>
                      <TableCell>
                        <Typography variant="body2Regular">{item?.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2Regular">{item?.tier__name}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2Regular">{item?.current_redeemed}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2Regular">{item?.coins_spent || 0}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </CustomTable>
            </TableContainer>
          </Paper>

          <AppPagination
            onPageChange={setCurrentPage}
            totalPages={Math.ceil((analytics?.cards_breakdown?.length || 0) / rowsPerPage)}
            currentPage={currentPage}
          />
        </>
      )}
    </Box>
  )
}

export default AnalyticsDashboard
